import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import view from './PhotoLiveSelfiePage.view';
import {
    GetTokenChallenge,
    GetUserRestrictionVisitor,
    ValidateSelfie,
    ValidateVideoPhoto,
    VeridasServiceProcess
} from "../../../../reduxToolkit/Visitor/VisitorApi";
import {
    setIsSuccessUserSelfie,
    setUserSelfie
} from "../../../../reduxToolkit/Visitor/VisitorSlice";
import FullPageLoader from '../../../../utils/FullPageLoader';
import { colors } from '../../../../enums/colors';
import useHandleBeforeUnload from '../../Functionality/useHandleBeforeUnload';
import useBackNavigation from '../../Functionality/useBackNavigation';
import { toast } from "react-toastify";
import {getFailedUrl, getSuccessUrl, getSdkLanguage, LIVENESS_MODEL, loadVdSdk} from "../../Model/Models";
import { registerInEventVeridas } from "../../../Dashboard/Events/External/Model/Model";
import { registerInBuildingVeridas } from "../../../Building/External/Model/Model";

/**
 * @component PhotoLiveSelfiePage
 * @description Handles the complete selfie/liveness capture flow using the Veridas VDAlive SDK.
 * Supports two modes depending on the `livenessVisitor` flag from user restrictions:
 * - **Passive liveness** (`livenessVisitor = false`): standard selfie capture via `ValidateSelfie`.
 * - **Active liveness** (`livenessVisitor = true`): head-movement challenge via `ValidateVideoPhoto`,
 *   requires a `challengeToken` obtained from `GetTokenChallenge`.
 *
 * @behavior
 * - Loads `vd-selfie.umd.js` on mount and injects `<vd-selfie>` into the DOM container.
 * - Patches SDK prototype stop-methods with try/catch to suppress known null-state crashes.
 * - Calls `silenceInstance()` before any `destroy()` or DOM removal to prevent the SDK's
 *   `stopRecordChallengeSinceHeadDetection` from crashing on active-recording null state.
 * - On `VD_restartProcess` (codes 8000–8002), fetches a new challenge token and resets
 *   the SDK element completely to avoid calling the crashing stop method.
 * - On `VD_capture`, stores `image` as `prefaceImage` and `image_alive` as `faceImage`.
 * - On `VD_closeButtonClicked`, silences the instance and navigates away via `handleLeave`.
 *
 * @param {string} validationId - Route param: Veridas validation process ID.
 * @param {string} [guardFlowId] - Route param: guard flow context.
 * @param {string} [userId] - Route param: visitor user ID.
 * @param {string} [eventId] - Route param: event ID, triggers `registerInEventVeridas` on success.
 * @param {string} [buildingId] - Route param: building of the registration, keeps the flow inside
 *   the building registration routes and triggers `registerInBuildingVeridas` on success.
 *
 * @author Cecilia López Martínez <clopez@ibl.mx> — IBL Full Stack Developer
 */
const PhotoLiveSelfiePage = () => {
    const { validationId, guardFlowId, userId, eventId, buildingId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const targetRef = useRef(null);

    const primaryColor = colors.primary800;
    const whiteColor = colors.general00;

    const tokenChallenge = useSelector((state) => state.VisitorSlice.getTokenChallenge);
    const userRestrictionData = useSelector((state) => state.VisitorSlice.getUserRestrictionVisitor);
    const { getInvitationById } = useSelector((state) => state.InvitationSlice);
    // Apartment of the building registration, resolved when the owner was validated
    const registrationResident = useSelector((state) => state.BuildingSlice.buildingRegistrationResident);

    const [prefaceImage, setPrefaceImage] = useState(null);
    const [faceImage, setFaceImage] = useState(null);
    const [vtt, setVtt] = useState(null);
    const [loading, setLoading] = useState(false);

    const vdSelfie = useRef(null);
    const sdkInitialized = useRef(false);
    const sdkElementMounted = useRef(false);
    const captureProcessing = useRef(false);

    // Refs always hold latest values so callbacks registered once never go stale.
    const userRestrictionDataRef = useRef(userRestrictionData);
    const tokenChallengeRef = useRef(tokenChallenge);

    // Keep refs in sync with the latest Redux values on every render so event callbacks
    // registered once (during SDK load) never close over stale restriction or token data.
    useEffect(() => {
        userRestrictionDataRef.current = userRestrictionData;
        tokenChallengeRef.current = tokenChallenge;
    });

    const invitationId = getInvitationById?.invitation?.id;
    const failedUrl = getFailedUrl({ invitationId, guardFlowId, userId, eventId, buildingId });
    const successUrl = getSuccessUrl({ invitationId, guardFlowId, userId, eventId, buildingId });
    const resolvedUserId = userId ? userId : getInvitationById?.invitation?.visitorId;

    // Fetch user restrictions on mount; challenge token is only needed for active liveness, so request it conditionally.
    useEffect(() => {
        dispatch(GetUserRestrictionVisitor()).then(({ payload: { data: { data } } }) => {
            if (data?.livenessVisitor === true) {
                dispatch(GetTokenChallenge(validationId));
            }
        });
    }, []);

    const tryInitSdk = () => {
        if (sdkInitialized.current) return;
        if (!sdkElementMounted.current) return;

        const userData = userRestrictionDataRef.current;
        if (!userData) return;

        const isActive = userData.livenessVisitor === true;
        const token = tokenChallengeRef.current;
        if (isActive && !token) return;

        const vdSelfieElement = targetRef.current?.querySelector('vd-selfie');
        if (!vdSelfieElement) return;

        sdkInitialized.current = true;
        vdSelfie.current = vdSelfieElement;

        const setup = {
            pathAssets: `${window.location.origin}/assets/selfie`,
            language: getSdkLanguage(i18n.language),
            liveness: isActive ? LIVENESS_MODEL.ACTIVE : LIVENESS_MODEL.PASSIVE,
            logoShow: false,
            closeButtonShow: true,
            instructionsShow: true,
            reviewShow: true,
            selectCamera: "front",
            detectionTimeout: 300000,
            helpModalShow: true,
            captureHeadingShow: true,
            cancelButtonInstructionsShow: true,
            instructionsHeadingShow: true,
            reviewHeadingShow: true
        };
        if (isActive) setup.challengeToken = token;

        const config = {
            setup,
            styles: {
                theme: {
                    brandPrimaryColor: primaryColor,
                },
                components: {
                    button: {
                        backgroundColorPrimary: primaryColor,
                        labelColorPrimary: whiteColor,
                        iconColorPrimary: whiteColor,
                        labelColorTertiary: primaryColor,
                        iconColorTertiary: primaryColor,
                    },
                    iconButton: {
                        iconColorTertiary: primaryColor,
                    },
                },
            },
        };

        try {
            const initPromise = isActive
                ? vdSelfieElement.initializeSdk(config, token)
                : vdSelfieElement.initializeSdk(config);

            if (initPromise && typeof initPromise.catch === 'function') {
                initPromise.catch((e) => {
                    sdkInitialized.current = false;
                    console.error(t('vd_selfie_sdk_init_rejected'), e?.message, JSON.stringify(e?.errors));
                    navigate(failedUrl);
                    toast.error(t('error_validating_the_information_please_try_again'));
                });
            }
        } catch (syncErr) {
            sdkInitialized.current = false;
            console.error(t('vd_sdk_init_sync_error'), syncErr?.message, syncErr);
            navigate(failedUrl);
            toast.error(t('error_validating_the_information_please_try_again'));
        }
    };

    const onVDSelfieMounted = () => {
        sdkElementMounted.current = true;
        tryInitSdk();
    };
    const onVDSelfieMountFailure = () => {};
    const onVDSelfieUnmounted = () => {};

    const isSdkStopBug = (reason) => {
        const stack = reason?.stack ?? '';
        const isTypeError = reason instanceof TypeError || reason?.name === 'TypeError';
        return (
            stack.includes('stopRecordChallengeSinceHeadDetection') ||
            (stack.includes('selfie.umd.js') && isTypeError)
        );
    };

    // Patches the SDK element INSTANCE so bound methods stored in the constructor
    // are replaced. Silences disconnectedCallback and stop methods before DOM removal
    // or explicit destroy to prevent null-state crash when recording is active.
    const silenceInstance = (element) => {
        if (!element) return;
        const noop = async () => {};
        for (const m of [
            'disconnectedCallback',
            'destroy',
            'stopRecordChallengeSinceHeadDetection',
            'stopRecordChallengeSinceFaceDetection',
            'stopRecordChallenge',
            'stopRecord',
        ]) {
            try { element[m] = noop; } catch (_) {}
        }
    };

    const onVDSelfieCloseButtonClicked = async () => {
        const element = vdSelfie.current;
        vdSelfie.current = null;
        silenceInstance(element);
        try {
            if (element?.destroy) await element.destroy();
            if (targetRef.current) targetRef.current.innerHTML = '';
        } catch (_) {}
        handleLeave();
    };

    const onVDSelfieCapture = (e) => {
        const data = e.detail.data;
        setPrefaceImage(data.image);
        setFaceImage(data.image_alive);
        setVtt(data.webVTT);
    };

    const onVDSelfieRestartProcess = async (e) => {
        if (![8000, 8001, 8002].includes(e.detail?.code)) return;
        try {
            const result = await dispatch(GetTokenChallenge(validationId));
            const newToken = result.payload?.data?.data;
            if (!newToken || !targetRef.current) return;
            const oldElement = vdSelfie.current;
            vdSelfie.current = null;
            silenceInstance(oldElement);
            sdkInitialized.current = false;
            sdkElementMounted.current = false;
            tokenChallengeRef.current = newToken;
            targetRef.current.innerHTML = '';
            targetRef.current.innerHTML = '<vd-selfie></vd-selfie>';
        } catch (err) {
            console.error(t('vd_restart_token_failed'), err);
        }
    };

    // Load SDK unconditionally on mount — no dependency on API data.
    useEffect(() => {
        const target = targetRef.current;
        if (!target) return;

        const onDepsError = (e) => console.error(t('vd_dependencies_loading_error'), e.detail);
        const onConfigError = (e) => console.error(t('vd_config_validation_error'), e.detail);
        // Suppress SDK bug: stopRecordChallengeSinceHeadDetection crashes on null state.
        // Also matches obfuscated builds where the function name is mangled but the error
        // is still a TypeError from selfie.umd.js (e.g. "Cannot destructure property ... null").
        const onUnhandledRejection = (e) => {
            if (isSdkStopBug(e.reason)) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };

        window.addEventListener('VD_mounted', onVDSelfieMounted);
        window.addEventListener('VD_mountFailure', onVDSelfieMountFailure);
        window.addEventListener('VD_unmounted', onVDSelfieUnmounted);
        window.addEventListener('VD_closeButtonClicked', onVDSelfieCloseButtonClicked);
        window.addEventListener('VD_capture', onVDSelfieCapture);
        window.addEventListener('VD_restartProcess', onVDSelfieRestartProcess);
        window.addEventListener('VD_dependenciesLoadingError', onDepsError);
        window.addEventListener('VD_configValidationError', onConfigError);
        window.addEventListener('unhandledrejection', onUnhandledRejection, { capture: true });

        const patchSdkStopMethods = () => {
            const stopMethods = [
                'stopRecordChallengeSinceHeadDetection',
                'stopRecordChallengeSinceFaceDetection',
                'stopRecordChallenge',
                'stopRecord',
            ];
            let proto = customElements.get('vd-selfie')?.prototype;
            while (proto && proto !== HTMLElement.prototype) {
                for (const method of stopMethods) {
                    if (typeof proto[method] === 'function') {
                        const original = proto[method];
                        proto[method] = async function (...args) {
                            try { return await original.apply(this, args); } catch (_) {}
                        };
                    }
                }
                proto = Object.getPrototypeOf(proto);
            }
        };

        let active = true;
        loadVdSdk('vd-selfie', '/assets/selfie/vd-selfie.umd.js', { type: 'module' })
            .then(() => {
                if (!active || !targetRef.current) return;
                patchSdkStopMethods();
                targetRef.current.innerHTML = '<vd-selfie></vd-selfie>';
            })
            .catch((e) => console.error(t('vd_sdk_load_failed'), e));

        return () => {
            active = false;
            sdkInitialized.current = false;
            sdkElementMounted.current = false;
            captureProcessing.current = false;
            window.removeEventListener('VD_mounted', onVDSelfieMounted);
            window.removeEventListener('VD_mountFailure', onVDSelfieMountFailure);
            window.removeEventListener('VD_unmounted', onVDSelfieUnmounted);
            window.removeEventListener('VD_closeButtonClicked', onVDSelfieCloseButtonClicked);
            window.removeEventListener('VD_capture', onVDSelfieCapture);
            window.removeEventListener('VD_restartProcess', onVDSelfieRestartProcess);
            window.removeEventListener('VD_dependenciesLoadingError', onDepsError);
            window.removeEventListener('VD_configValidationError', onConfigError);
            window.removeEventListener('unhandledrejection', onUnhandledRejection, { capture: true });
            // StrictMode: don't clear innerHTML — cleanup runs before remount and would wipe the re-inserted element.
            const elementToDestroy = vdSelfie.current;
            vdSelfie.current = null;
            // Silence instance before destroy so disconnectedCallback and stop methods
            // are no-ops — prevents null-state crash regardless of whether methods are
            // bound in the constructor or on the prototype.
            silenceInstance(elementToDestroy);
            if (elementToDestroy?.destroy) {
                elementToDestroy.destroy().catch(() => {});
            }
        };
    }, []);

    // When API data arrives after SDK element is already mounted, trigger init.
    useEffect(() => {
        tryInitSdk();
    }, [userRestrictionData, tokenChallenge]);

    // Trigger save once capture is complete: passive liveness needs prefaceImage only; active needs all three fields.
    useEffect(() => {
        if (!prefaceImage) return;
        const isActive = userRestrictionDataRef.current?.livenessVisitor === true;
        if (isActive && (!faceImage || !vtt)) return;
        destroyCameraAndSave();
    }, [prefaceImage, faceImage, vtt]);

    const handleDestroy = async () => {
        try {
            if (vdSelfie.current && typeof vdSelfie.current.destroy === 'function') {
                silenceInstance(vdSelfie.current);
                await vdSelfie.current.destroy();
            }
            if (targetRef.current) {
                targetRef.current.innerHTML = '';
            }
        } catch (e) {
            console.warn(t('vd_alive_cleanup_failed'), e);
        }
    };

    const handleFailed = () => {
        setLoading(false);
        navigate(failedUrl);
        toast.error(t('error_validating_the_information_please_try_again'));
    };

    const selfieValidationStatus = async (validationId) => {
        const { payload } = await dispatch(
            VeridasServiceProcess({ validationId, userId: resolvedUserId })
        );
        const { data, success } = payload?.data ?? {};
        if (success) {
            dispatch(setUserSelfie(data));
            dispatch(setIsSuccessUserSelfie(success));
            if (buildingId) {
                registerInBuildingVeridas({
                    dispatch,
                    resolvedUserId,
                    buildingId,
                    buildingApartmentId: registrationResident?.buildingApartmentId,
                    setLoading,
                    t,
                    navigate,
                    successUrl,
                    data,
                    validationId
                });
                return;
            }
            if (eventId) {
                registerInEventVeridas({
                    dispatch,
                    resolvedUserId,
                    eventId,
                    setLoading,
                    t,
                    navigate,
                    successUrl,
                    data,
                    validationId
                });
                return;
            }
            setLoading(false);
            navigate(successUrl);
        } else {
            handleFailed();
        }
    };

    const sendValidateSelfie = async () => {
        if (!validationId) return;
        setLoading(true);
        const body = { image: prefaceImage, imageAlive: prefaceImage, validationId };
        const { payload } = await dispatch(ValidateSelfie(body));
        payload?.data?.success ? await selfieValidationStatus(validationId) : handleFailed();
    };

    const sendValidateVideoPhoto = async () => {
        if (!tokenChallengeRef.current || !validationId) return;
        setLoading(true);
        const body = {
            annotations: vtt,
            selfie: prefaceImage,
            video: faceImage,
            challenge: tokenChallengeRef.current,
            validationId,
            userId: resolvedUserId
        };
        const { payload } = await dispatch(ValidateVideoPhoto(body));
        payload?.data?.success ? await selfieValidationStatus(validationId) : handleFailed();
    };

    const destroyCameraAndSave = async () => {
        if (captureProcessing.current) return;
        captureProcessing.current = true;
        const isActive = userRestrictionDataRef.current?.livenessVisitor === true;
        setLoading(true);
        await handleDestroy();
        try {
            if (isActive && vtt) {
                await sendValidateVideoPhoto();
            } else if (!isActive) {
                await sendValidateSelfie();
            } else {
                handleFailed();
            }
        } catch (err) {
            console.error(t('vd_camera_save_error'), err);
            handleFailed();
        }
    };

    useBackNavigation({ getInvitationById, validationId });
    const { handleLeave } = useHandleBeforeUnload({ getInvitationById, validationId });

    return loading ? <FullPageLoader /> : view({ targetRef });
};

export default PhotoLiveSelfiePage;
